import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, BadgeCheck } from 'lucide-react';
import { PROJECTS } from '../data/site';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'ongoing', label: 'Ongoing' },
];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const active = params.get('status') || 'all';
  const list = PROJECTS.filter((p) => active === 'all' || p.status.toLowerCase() === active);

  return (
    <main data-testid="projects-page" className="pt-36 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">OUR WORK — PLOTTED DEVELOPMENTS</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter text-5xl sm:text-7xl text-ink"
          >
            Projects
          </motion.h1>
          <div className="flex gap-2 flex-wrap" data-testid="project-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setParams(f.key === 'all' ? {} : { status: f.key })}
                data-testid={`filter-${f.key}`}
                className={`text-[11px] tracking-[0.18em] font-medium px-6 py-3 border transition-colors duration-300 ${
                  active === f.key
                    ? 'bg-moss text-sand border-moss'
                    : 'border-clay text-ink/60 hover:border-terra hover:text-terra'
                }`}
              >
                {f.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="group"
              data-testid={`projects-card-${p.id}`}
            >
              <Link to={`/projects/${p.id}`} className="block">
                <div className="relative overflow-hidden aspect-[4/3] bg-stone">
                  <img src={p.image} alt={p.name} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-bark/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className={`absolute top-4 left-4 text-[10px] tracking-[0.18em] font-medium px-3 py-1.5 ${p.status === 'Completed' ? 'bg-sand/90 text-ink' : 'bg-terra text-white'}`}>
                    {p.status.toUpperCase()}
                  </span>
                  <span className="absolute bottom-4 right-4 w-10 h-10 bg-terra text-white flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
                <div className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-heading font-bold text-xl tracking-tight text-ink group-hover:text-terra transition-colors duration-300">{p.name}</h3>
                    <span className="text-[10px] tracking-[0.2em] text-ink/40 font-medium mt-1.5 whitespace-nowrap">VIEW DETAILS</span>
                  </div>
                  <p className="mt-1.5 text-xs text-ink/50 flex items-center gap-1.5"><MapPin size={11} className="text-terra" /> {p.location}</p>
                  <p className="mt-2 text-xs text-ink/60 flex items-center gap-1.5"><BadgeCheck size={12} className="text-terra" /> {p.approval}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
